import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FeatherIcon } from '../components/FeatherIcon';
import { Spinner } from '../components/Spinner';
import { canRead, canWrite } from '../crm/crmRail';
import { useCrmPermissions } from '../crm/useCrmPermissions';
import { AccountFanslyDeps } from '../fansly/FanslyDeps';
import { ChatView } from '../fansly/chats/ChatView';
import { findChatRow } from '../fansly/chats/findChat';
import type { ChatRow } from '../fansly/chats/model';
import { useFanslyApi } from '../fansly/deps';
import { useMe } from '../fansly/useMe';
import '../fansly/chats/chats.css';
import type { OpenChat } from './ChatPane';

interface Props {
  chat: OpenChat;
  liveTick: number;
  pinned: boolean;
  onTogglePin: () => void;
  /** Phones: back to Workspace's chats list. */
  onList?: () => void;
  compact: boolean;
}

/**
 * A Fansly chat in Workspace's pane (ADR 0045): the Fansly Chats page's own
 * chat view and side column, with Workspace's pin and "open in CRM" in its header.
 */
export function FanslyChatPane(props: Props) {
  return (
    <AccountFanslyDeps accountId={props.chat.accountId}>
      <FanslyChat {...props} />
    </AccountFanslyDeps>
  );
}

function FanslyChat({ chat, liveTick, pinned, onTogglePin, onList, compact }: Props) {
  const { t } = useTranslation('workspace');
  const navigate = useNavigate();
  const api = useFanslyApi();
  const me = useMe();
  const meId = me?.id ?? '';
  const perms = useCrmPermissions(chat.accountId);
  const can = {
    writeChats: canWrite(perms, 'chats'),
    readLists: canRead(perms, 'collections'),
    writeLists: canWrite(perms, 'collections'),
    readStats: canRead(perms, 'statistics'),
  };
  const [found, setFound] = useState<{ fanId: string; row: ChatRow | null } | null>(null);

  useEffect(() => {
    if (!meId || !perms) return;
    let live = true;
    setFound(null);
    findChatRow(api, chat.fanId, meId, canRead(perms, 'collections'))
      .then((row) => live && setFound({ fanId: chat.fanId, row }))
      .catch(() => live && setFound({ fanId: chat.fanId, row: null }));
    return () => {
      live = false;
    };
  }, [api, chat.fanId, meId, perms]);

  const row = found?.fanId === chat.fanId ? found.row : undefined;
  if (row === undefined) {
    return (
      <div className="ws-pane-fansly-empty">
        <Spinner size={22} label={t('pane.loading')} />
      </div>
    );
  }
  if (!row) return <div className="ws-pane-fansly-empty">{t('pane.noFanslyChat')}</div>;

  const tools = (
    <>
      <button type="button" className={`chat-head-icon-btn ${pinned ? 'active' : ''}`} onClick={onTogglePin} title={pinned ? t('pane.unpin') : t('pane.pin')} aria-label={pinned ? t('pane.unpin') : t('pane.pin')} aria-pressed={pinned}>
        <FeatherIcon name="pin" size={18} />
      </button>
      <button
        type="button"
        className="chat-head-icon-btn"
        onClick={() => navigate(`/account/${encodeURIComponent(chat.accountId)}/crm/chats?fan=${encodeURIComponent(chat.fanId)}`)}
        title={t('pane.openInCrm')}
        aria-label={t('pane.openInCrm')}
      >
        <FeatherIcon name="external-link" size={18} />
      </button>
    </>
  );

  return (
    <div className="cp-chat ws-pane-fansly">
      <ChatView
        key={`${chat.accountId}:${row.groupId}`}
        row={row}
        unreadOnOpen={row.unread > 0}
        meId={meId}
        can={can}
        phone={compact}
        onBack={onList}
        onHidden={() => onList?.()}
        onActivity={() => undefined}
        onPatch={(patch) => setFound((f) => (f?.row ? { ...f, row: { ...f.row, ...patch } } : f))}
        tools={tools}
        liveTick={liveTick}
      />
    </div>
  );
}
